<template>
  <div>
    <page-header :title="title" :container-class="containerClass" />
    <div class="page-section">
      <div :class="containerClass">
        <div class="card mb-0">
          <div class="card-header">
            <div class="row align-items-center" style="white-space: nowrap">
              <!-- Search -->
              <div class="col-lg-auto">
                <form class="search-form search-form--light d-lg-inline-flex mb-8pt mb-lg-0" @submit.prevent="">
                  <b-form-input
                    class="w-lg-auto"
                    placeholder="Search Templates"
                    v-model="searchTerm"
                    @input="onSearch"
                  />

                  <b-btn variant="flush" type="submit">
                    <md-icon v-text="'search'" />
                  </b-btn>
                </form>
              </div>

              <!-- Filters and Add Btn -->
              <div class="col-lg d-flex flex-wrap justify-content-end">
                <div class="col-lg-6 mx-0 px-0"></div>
                <b-btn class="ml-2" :to="{ name: 'add-template' }" exact variant="primary"> Add Template </b-btn>
              </div>
            </div>
          </div>

          <!-- Schools Table -->
          <b-table
            :fields="tableColumns"
            :items="allTemplates"
            :busy="isLoading"
            head-variant="light"
            class="table-nowrap"
            hover
            responsive
            no-local-sorting
            @sort-changed="onSortChange"
          >
            <template #cell(image)="data">
              <div class="d-flex align-items-center">
                <fmv-avatar :title="true" title-class="bg-transparent" rounded no-link slot="aside" class="mr-2">
                  <b-img :src="data.item.image" class="img-fluid" width="40" alt="Logo" v-if="data.item.image" />
                  <i class="fas fa-university fa-lg" v-else></i>
                </fmv-avatar></div
            ></template>

            <template #cell(description)="data">
              {{
                truncate(data.item.description, {
                  length: 40,
                })
              }}
            </template>

            <template #cell(category)="data">
              {{ $t(`categoryTypes.${data.item.category}`) }}
            </template>

            <template #cell(is_active)="data">
              <b-badge
                :class="`border border-${data.item.is_active ? 'success' : 'danger'} text-${
                  data.item.is_active ? 'success' : 'danger'
                }`"
                variant="none"
                >{{ data.item.is_active ? 'Active' : 'Inactive' }}</b-badge
              >
            </template>
            <template #head(actions)="">
              <span></span>
            </template>
            <template #cell(actions)="data">
              <router-link
                :to="{
                  name: 'edit-template',
                  params: { id: data.item.id },
                }"
                class="svg-icon mr-2"
                v-b-popover.hover.right
                :title="'Edit'"
              >
                <i class="material-icons">edit</i>
              </router-link>

              <a href="#" @click.prevent="updateStatus(data.index)" v-b-popover.hover.right :title="'Update Status'">
                <md-icon :class="data.item.is_active ? 'text-danger' : 'text-success'">{{
                  data.item.is_active ? 'block' : 'check_circle_outline'
                }}</md-icon>
              </a>

              <a
                href="#"
                @click.prevent="removeTemplate(data.item.id)"
                v-b-popover.hover.right
                :title="'Delete Template'"
              >
                <i class="material-icons text-danger">delete</i>
              </a>
            </template>
          </b-table>

          <!-- Footer Pagination -->
          <div class="card-footer">
            <pagination
              v-model="currentPage"
              :total-rows="totalTemplates"
              :per-page="perPage"
              @change="onPageChange"
              aria-controls="schools-table"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import PageHeader from '@/components/Ui/PageHeader.vue';
import { debounce, truncate } from 'lodash';
import { mapActions } from 'vuex';

import Page from '@/components/Page.vue';
import Pagination from '@/components/Ui/Pagination.vue';
import MdIcon from '@/components/Ui/MdIcon.vue';
import { DEFAULT_PAGE_SIZE } from '@/common/constants';
import { FmvAvatar } from 'fmv-avatar';
export default {
  components: { MdIcon, PageHeader, Pagination, FmvAvatar },
  extends: Page,

  data() {
    return {
      title: 'Manage Templates',
      areProgramsLoading: false,
      program: null,
      allPrograms: [],
      isLoading: false,
      perPage: DEFAULT_PAGE_SIZE,
      currentPage: 1,
      totalTemplates: 0,
      searchTerm: '',
      allTemplates: [],
    };
  },

  computed: {
    breadcrumb() {
      return [
        { text: this.$t('home'), to: this.routes.home },
        { text: 'Manage Templates', active: true },
      ];
    },

    tableColumns() {
      return [
        { key: 'image', label: '' },
        { key: 'title', label: 'Title' },
        { key: 'description', label: 'Description' },
        { key: 'category', label: 'Category' },

        { key: 'is_active', label: 'Status' },
        {
          key: 'actions',
          tdClass: 'text-right',
          thStyle: { minWidth: '100px' },
        },
      ];
    },
  },

  methods: {
    ...mapActions('myAI', ['getAllTemplates', 'deleteTemplate', 'updateTemplate']),
    truncate,
    onFilterApplied() {
      this.fetchTemplates();
    },
    async fetchTemplates(pageNum = 1, params = {}) {
      this.isLoading = true;
      document.getElementById('app').scrollIntoView();

      const response = await this.getAllTemplates({
        limit: this.perPage,
        offset: (pageNum - 1) * this.perPage,
        ...(this.ordering && { ordering: this.ordering }),
        ...(this.searchTerm && { search: this.searchTerm }),
        ...params,
      });
      this.allTemplates = response.data.results;
      this.currentPage = pageNum;
      this.totalTemplates = response.data.count;
      this.isLoading = false;
    },

    onPageChange(pageNum) {
      this.fetchTemplates(pageNum);
    },

    onSortChange(context) {
      this.ordering = context.sortDesc ? '-' + context.sortBy : context.sortBy;
      this.fetchTemplates();
    },

    onSearch() {
      this.debouncedSearchStates(this);
    },

    debouncedSearchStates: debounce((vm) => {
      vm.fetchTemplates();
    }, 500),
    async updateStatus(index) {
      this.isLoading = true;
      const currStatus = this.allTemplates[index].is_active;
      try {
        const isConfirmed = await this.$bvModal.msgBoxConfirm(
          `Please confirm that you want to mark the status ${currStatus ? 'Inactive' : 'Active'}.`,
          {
            title: 'Are you sure?',
            size: 'md',
            buttonSize: 'sm',
            okVariant: 'danger',
            okTitle: 'Yes',
            cancelTitle: 'No',
            footerClass: 'p-2',
            hideHeaderClose: false,
            centered: true,
          }
        );
        if (isConfirmed) {
          await this.updateTemplate({ id: this.allTemplates[index].id, data: { is_active: !currStatus } });
          this.fetchTemplates();
          this.makeToast({ variant: 'success', msg: 'Status changed successfully!' });
        }
      } catch (error) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
      this.isLoading = false;
    },
    async removeTemplate(id) {
      this.isLoading = true;
      try {
        const isConfirmed = await this.$bvModal.msgBoxConfirm(
          `Please confirm that you want to delete the selected template.`,
          {
            title: 'Are you sure?',
            size: 'md',
            buttonSize: 'sm',
            okVariant: 'danger',
            okTitle: 'Yes',
            cancelTitle: 'No',
            footerClass: 'p-2',
            hideHeaderClose: false,
            centered: true,
          }
        );
        if (isConfirmed) {
          await this.deleteTemplate(id);
          this.fetchTemplates();
          this.makeToast({ variant: 'success', msg: 'Template deleted successfully!' });
        }
      } catch (error) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
      this.isLoading = false;
    },
  },

  async mounted() {
    this.fetchTemplates();
  },
};
</script>
